import { useEffect, useRef, useState } from "react";
import { academyRequest } from "./academyApi";
import { useIdentity } from "./useIdentity";

export type CoreNotification = {
  id: string;
  category: string;
  title: string;
  body: string;
  deepLink: string;
  createdAt: string;
  readAt: string | null;
};
export function useCoreNotifications() {
  const identity = useIdentity();
  const accountId = identity.session?.user.id;
  const [notifications, setNotifications] = useState<CoreNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const pendingReads = useRef(new Set<string>());
  useEffect(() => {
    if (!accountId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    let active = true;
    const refresh = async () => {
      try {
        const [feed, count] = await Promise.all([
          academyRequest<{ items: CoreNotification[]; nextCursor: string | null }>("me/notifications"),
          academyRequest<{ count: number }>("me/notifications/unread-count"),
        ]);
        if (!active) return;
        setNotifications(feed.items);
        setNextCursor(feed.nextCursor);
        setUnreadCount(count.count);
        setError(false);
      } catch {
        if (active) setError(true);
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 15000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [accountId]);
  const markRead = async (id: string) => {
    if (pendingReads.current.has(id)) return;
    pendingReads.current.add(id);
    try {
      const result = await academyRequest<{ readAt: string | null }>(
        "me/notifications/" + encodeURIComponent(id) + "/read",
        {},
        "POST",
      );
      if (result.readAt)
        setNotifications(current =>
          current.map(item => (item.id === id ? { ...item, readAt: item.readAt ?? result.readAt } : item)),
        );
      const count = await academyRequest<{ count: number }>("me/notifications/unread-count");
      setUnreadCount(count.count);
    } finally {
      pendingReads.current.delete(id);
    }
  };
  const markAllRead = async () => {
    await academyRequest("me/notifications/read-all", {}, "POST");
    setNotifications(current =>
      current.map(item => ({ ...item, readAt: item.readAt ?? new Date().toISOString() })),
    );
    setUnreadCount(0);
  };
  const loadMore = async () => {
    if (!nextCursor) return;
    const feed = await academyRequest<{ items: CoreNotification[]; nextCursor: string | null }>(
      "me/notifications?cursor=" + encodeURIComponent(nextCursor),
    );
    setNotifications(current => [...current, ...feed.items]);
    setNextCursor(feed.nextCursor);
  };
  return { notifications, unreadCount, nextCursor, error, markRead, markAllRead, loadMore };
}
